import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Loader2,
  Instagram,
  Facebook,
  Twitter,
  Youtube,
  Linkedin,
  Video,
  Check,
  X,
  Mic2,
  ArrowRight,
  Image as ImageIcon,
  CalendarClock,
} from "lucide-react";
import { api } from "@/lib/api";
import { readApiError } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const PLATFORMS = [
  { id: "instagram", label: "Instagram", icon: Instagram },
  { id: "tiktok", label: "TikTok", icon: Video },
  { id: "facebook", label: "Facebook", icon: Facebook },
  { id: "x", label: "X", icon: Twitter },
  { id: "youtube", label: "YouTube", icon: Youtube },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin },
];

// Ayrshare error codes 439/139 — these platforms reject text-only posts.
const MEDIA_REQUIRED = ["instagram", "tiktok", "youtube"];

const requiredButMissingMedia = (selected, imageUrl) =>
  !imageUrl && selected.some((p) => MEDIA_REQUIRED.includes(p));

const mediaRequiredLabels = (selected) =>
  selected
    .filter((p) => MEDIA_REQUIRED.includes(p))
    .map((p) => ({ instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube" }[p]))
    .join(" / ");

const FORMATS = [
  { id: "caption", label: "Caption" },
  { id: "hook", label: "Hook" },
  { id: "script", label: "Script" },
  { id: "story", label: "Story" },
];

const defaultScheduleISO = () => {
  const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
  d.setMinutes(0, 0, 0);
  return d.toISOString().slice(0, 16); // for <input type=datetime-local>
};

function PlatformCard({ platform, state, onRetry }) {
  const Icon = platform.icon;
  const badge = {
    idle: { label: "waiting", cls: "text-[#9A8E78] border-[#5E594E]" },
    loading: { label: "generating…", cls: "text-[#C9A961] border-[#C9A961]/50" },
    ready: { label: "ready", cls: "text-[#7A8B6F] border-[#7A8B6F]/60" },
    scheduled: { label: "scheduled", cls: "text-[#C9A961] border-[#C9A961]" },
    failed: { label: "failed", cls: "text-[#B85A4A] border-[#B85A4A]/60" },
  }[state?.status || "idle"];

  return (
    <div
      data-testid={`composer-preview-${platform.id}`}
      className={`rounded-2xl border bg-[#5A5347] p-4 transition-all ${
        state?.status === "loading" ? "border-[#C9A961]/50 animate-pulse" : "border-[#5E594E]"
      }`}
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-sm text-[#F5EFE2]">
          <Icon className="h-4 w-4 text-[#C9A961]" />
          {platform.label}
        </div>
        <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${badge.cls}`}>
          {badge.label}
        </span>
      </div>
      {!state || state.status === "idle" || state.status === "loading" ? (
        <div className="space-y-2">
          <div className="h-3 w-full animate-pulse rounded bg-[#5E594E]/70" />
          <div className="h-3 w-11/12 animate-pulse rounded bg-[#5E594E]/70" />
          <div className="h-3 w-2/3 animate-pulse rounded bg-[#5E594E]/70" />
          <div className="mt-3 h-3 w-1/3 animate-pulse rounded bg-[#5E594E]/50" />
        </div>
      ) : state.status === "failed" ? (
        <div className="text-xs text-[#B85A4A]">
          <p className="mb-2 line-clamp-3">{state.error || "Unknown error"}</p>
          {onRetry && (
            <button
              type="button"
              onClick={() => onRetry(platform.id)}
              className="rounded border border-[#B85A4A]/60 px-2 py-1 text-[10px] uppercase hover:bg-[#B85A4A]/10"
            >
              Retry
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2 text-sm text-[#F5EFE2]/90">
          {state.hook && (
            <p className="font-serif text-base leading-snug text-[#F5EFE2]">"{state.hook}"</p>
          )}
          <p className="whitespace-pre-line text-xs leading-relaxed text-[#C9BEA8] line-clamp-6">
            {state.content}
          </p>
          {state.hashtags?.length > 0 && (
            <p className="pt-1 text-[10px] text-[#C9A961]">
              {state.hashtags.slice(0, 6).map((h) => `#${h}`).join(" ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminComposer() {
  const [selected, setSelected] = useState(["instagram", "tiktok", "facebook"]);
  const [topic, setTopic] = useState("morning pruning routine with a juniper bonsai");
  const [format, setFormat] = useState("caption");
  const [imageUrl, setImageUrl] = useState("");
  const [voiceOn, setVoiceOn] = useState(true);
  const [schedule, setSchedule] = useState(defaultScheduleISO());
  const [library, setLibrary] = useState([]);
  const [imageOpen, setImageOpen] = useState(false);

  const [running, setRunning] = useState(false);
  const [previews, setPreviews] = useState({});
  const [stage, setStage] = useState("idle"); // idle · copy · voice · schedule · done
  const [voiceClip, setVoiceClip] = useState(null);
  const [scheduleResult, setScheduleResult] = useState(null);
  const [flourish, setFlourish] = useState(false);

  useEffect(() => {
    api.get("/admin/library/images").then((r) => setLibrary(r.data.images || [])).catch(() => {});
  }, []);

  const togglePlatform = (id) =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const runOne = async (platform) => {
    setPreviews((p) => ({ ...p, [platform]: { status: "loading" } }));
    try {
      const res = await api.post("/admin/composer/generate-copy", {
        platform,
        topic: topic.trim(),
        format,
      });
      setPreviews((p) => ({
        ...p,
        [platform]: { status: "ready", ...res.data },
      }));
      return res.data;
    } catch (e) {
      setPreviews((p) => ({
        ...p,
        [platform]: { status: "failed", error: readApiError(e) },
      }));
      return null;
    }
  };

  const run = async () => {
    if (!selected.length) return toast.error("Pick at least one platform");
    if (!topic.trim()) return toast.error("Give it a topic");
    if (requiredButMissingMedia(selected, imageUrl)) {
      return toast.error(
        `${mediaRequiredLabels(selected)} require an image`,
        { description: "Pick one from the library or unselect those platforms." }
      );
    }
    const scheduleISO = new Date(schedule).toISOString();
    if (new Date(scheduleISO).getTime() - Date.now() < 15 * 60 * 1000) {
      return toast.error("Schedule must be at least 15 minutes in the future");
    }

    setRunning(true);
    setStage("copy");
    setPreviews(Object.fromEntries(selected.map((id) => [id, { status: "loading" }])));
    setVoiceClip(null);
    setScheduleResult(null);
    setFlourish(false);

    // Stage 1 — kick off every platform in parallel; each card fills as its promise resolves.
    const results = await Promise.all(selected.map(runOne));
    const successful = results.filter(Boolean);
    if (successful.length === 0) {
      toast.error("All platform generations failed. See cards.");
      setStage("idle");
      setRunning(false);
      return;
    }

    // Combined draft text = the longest one (usually script/caption).
    const combinedText = successful
      .map((r) => r.content)
      .sort((a, b) => (b?.length || 0) - (a?.length || 0))[0] || "";

    // Stage 2 — voice
    let clip = null;
    if (voiceOn) {
      setStage("voice");
      try {
        const res = await api.post("/admin/composer/generate-voice", {
          text: combinedText.slice(0, 1500),
          label: `composer_${Date.now()}`,
        });
        clip = res.data;
        setVoiceClip(clip);
      } catch (e) {
        toast.warning("Voice failed", { description: readApiError(e) });
      }
    }

    // Stage 3 — Ayrshare schedule (one call, all platforms at once)
    setStage("schedule");
    try {
      const res = await api.post("/admin/composer/schedule", {
        content: combinedText.slice(0, 2500),
        platforms: selected,
        schedule_date_iso: scheduleISO,
        media_urls: imageUrl ? [imageUrl] : [],
      });
      setScheduleResult(res.data);
      setPreviews((p) => {
        const next = { ...p };
        selected.forEach((id) => {
          if (next[id]?.status === "ready") next[id] = { ...next[id], status: "scheduled" };
        });
        return next;
      });
      setFlourish(true);
      setTimeout(() => setFlourish(false), 2200);
      toast.success("Scheduled", { description: `${selected.length} platform${selected.length > 1 ? "s" : ""} · Ayrshare accepted` });
    } catch (e) {
      // Surface Ayrshare error verbatim per Kenny's locked rule
      const err = e?.response?.data?.detail;
      const errStr = typeof err === "string" ? err : JSON.stringify(err);
      toast.error("Ayrshare rejected the post", { description: errStr?.slice(0, 400) || readApiError(e) });
      setScheduleResult({ error: errStr });
    }

    setStage("done");
    setRunning(false);
  };

  const StageChip = ({ id, label }) => {
    const done = ["copy", "voice", "schedule", "done"].indexOf(stage) > ["copy", "voice", "schedule", "done"].indexOf(id);
    const active = stage === id;
    return (
      <div
        data-testid={`composer-stage-${id}`}
        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${
          done
            ? "border-[#7A8B6F]/50 text-[#7A8B6F]"
            : active
            ? "border-[#C9A961] text-[#C9A961]"
            : "border-[#5E594E] text-[#9A8E78]"
        }`}
      >
        {done ? <Check className="h-3 w-3" /> : active ? <Loader2 className="h-3 w-3 animate-spin" /> : <span className="h-2 w-2 rounded-full bg-current opacity-40" />}
        {label}
      </div>
    );
  };

  return (
    <div className="space-y-8" data-testid="admin-composer">
      <div>
        <p className="admin-eyebrow">Composer</p>
        <h1 className="admin-h1 mt-2">
          One click. One post. Six platforms.
        </h1>
        <p className="mt-2 text-sm text-[#C9BEA8]">
          AI copy → Daniel voice → Ayrshare schedule. Watch the platform cards fill in live.
        </p>
      </div>

      {/* Form */}
      <div className="grid gap-6 lg:grid-cols-[1fr_1.3fr]">
        <div className="space-y-6 rounded-2xl border border-[#5E594E] bg-[#5A5347] p-6">
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-[#9A8E78]">Platforms</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {PLATFORMS.map((p) => {
                const on = selected.includes(p.id);
                const Icon = p.icon;
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => togglePlatform(p.id)}
                    data-testid={`composer-platform-${p.id}`}
                    disabled={running}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                      on
                        ? "border-[#C9A961] bg-[#C9A961] text-[#3D3A33]"
                        : "border-[#5E594E] bg-[#3D3A33] text-[#C9BEA8] hover:border-[#C9A961]/60 hover:text-[#C9A961]"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label htmlFor="composer-topic" className="text-xs uppercase tracking-[0.18em] text-[#9A8E78]">Topic</Label>
            <Textarea
              id="composer-topic"
              data-testid="composer-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={running}
              rows={3}
              className="mt-1 border-[#5E594E] bg-[#3D3A33] text-[#F5EFE2] placeholder:text-[#9A8E78]"
              placeholder="e.g., morning pruning routine with a juniper bonsai"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-[#9A8E78]">Format</Label>
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                {FORMATS.map((f) => (
                  <button
                    type="button"
                    key={f.id}
                    disabled={running}
                    onClick={() => setFormat(f.id)}
                    data-testid={`composer-format-${f.id}`}
                    className={`rounded-md border px-2 py-1 text-xs transition-colors ${
                      format === f.id
                        ? "border-[#C9A961] bg-[#C9A961] text-[#3D3A33]"
                        : "border-[#5E594E] bg-[#3D3A33] text-[#C9BEA8] hover:border-[#C9A961]/60"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="composer-schedule" className="text-xs uppercase tracking-[0.18em] text-[#9A8E78]">Schedule</Label>
              <Input
                id="composer-schedule"
                data-testid="composer-schedule"
                type="datetime-local"
                value={schedule}
                onChange={(e) => setSchedule(e.target.value)}
                disabled={running}
                className="mt-2 border-[#5E594E] bg-[#3D3A33] text-[#F5EFE2]"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-[#9A8E78]">Image</Label>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {imageUrl ? (
                <>
                  <img src={imageUrl} alt="" className="h-14 w-14 rounded border border-[#5E594E] object-cover" />
                  <button type="button" onClick={() => setImageUrl("")} className="text-xs text-[#B85A4A] hover:underline">
                    Remove
                  </button>
                </>
              ) : (
                <span className="text-xs text-[#9A8E78]">No image</span>
              )}
              <button
                type="button"
                data-testid="composer-image-picker"
                onClick={() => setImageOpen((v) => !v)}
                disabled={running}
                className="inline-flex items-center gap-1 rounded-md border border-[#5E594E] px-2 py-1 text-xs text-[#C9BEA8] hover:border-[#C9A961]/60 hover:text-[#C9A961]"
              >
                <ImageIcon className="h-3 w-3" />
                {imageOpen ? "Close" : "Pick from library"}
              </button>
            </div>
            {imageOpen && (
              <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-[#5E594E] bg-[#3D3A33] p-2">
                <div className="grid grid-cols-4 gap-1.5">
                  {library.slice(0, 24).map((img) => (
                    <button
                      type="button"
                      key={img.id}
                      onClick={() => {
                        setImageUrl(img.url);
                        setImageOpen(false);
                      }}
                      className={`overflow-hidden rounded border ${
                        imageUrl === img.url ? "border-[#C9A961]" : "border-[#5E594E] hover:border-[#C9A961]/50"
                      }`}
                      title={img.name}
                    >
                      <img src={img.url} alt={img.name} className="aspect-square w-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between rounded-lg border border-[#5E594E] bg-[#3D3A33] p-3">
            <div className="flex items-center gap-2 text-sm text-[#F5EFE2]">
              <Mic2 className="h-4 w-4 text-[#C9A961]" />
              Voiceover (Daniel)
            </div>
            <button
              type="button"
              onClick={() => setVoiceOn((v) => !v)}
              data-testid="composer-voice-toggle"
              disabled={running}
              className={`relative h-6 w-11 rounded-full transition-colors ${voiceOn ? "bg-[#C9A961]" : "bg-[#5E594E]"}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-[#3D3A33] transition-all ${voiceOn ? "left-5" : "left-0.5"}`} />
            </button>
          </div>

          <Button
            onClick={run}
            disabled={running || requiredButMissingMedia(selected, imageUrl)}
            data-testid="composer-run-btn"
            className="w-full bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
            size="lg"
          >
            {running ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Working…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Compose &amp; Schedule →
              </>
            )}
          </Button>
          {requiredButMissingMedia(selected, imageUrl) && (
            <p
              data-testid="composer-media-warning"
              className="mt-1 rounded-md border border-[#B85A4A]/50 bg-[#B85A4A]/10 px-3 py-2 text-xs text-[#E8B4A6]"
            >
              <strong className="text-[#F5EFE2]">{mediaRequiredLabels(selected)}</strong> require an
              image. Pick one from the library or unselect those platforms.
            </p>
          )}
        </div>

        {/* Live preview grid */}
        <div className={`space-y-4 ${flourish ? "ring-2 ring-[#C9A961]/60 ring-offset-4 ring-offset-[#3D3A33] rounded-2xl transition-all" : ""}`}>
          {running || stage !== "idle" ? (
            <div className="flex flex-wrap items-center gap-2" data-testid="composer-progress">
              <StageChip id="copy" label="Generating copy" />
              <StageChip id="voice" label={voiceOn ? "Recording voiceover" : "Voice off"} />
              <StageChip id="schedule" label={`Scheduling to ${selected.length}`} />
            </div>
          ) : (
            <p className="text-xs text-[#9A8E78]">Pick platforms + topic, then hit Compose to see this fill in live.</p>
          )}

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {selected.map((id) => {
              const p = PLATFORMS.find((x) => x.id === id);
              return <PlatformCard key={id} platform={p} state={previews[id]} onRetry={runOne} />;
            })}
          </div>

          {voiceClip && (
            <div
              data-testid="composer-voice-result"
              className="rounded-2xl border border-[#5E594E] bg-[#5A5347] p-4"
            >
              <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#C9A961]">
                <Mic2 className="h-3 w-3" />
                Voiceover · {voiceClip.voice_name} · ~{Math.round(voiceClip.duration_estimate_sec || 0)}s
              </div>
              <audio
                data-testid="composer-voice-audio"
                controls
                src={`data:audio/mpeg;base64,${voiceClip.audio_base64}`}
                className="w-full"
              />
            </div>
          )}

          {stage === "done" && !scheduleResult?.error && (
            <div className="rounded-2xl border border-[#7A8B6F]/60 bg-[#7A8B6F]/10 p-4 text-sm text-[#F5EFE2]">
              <p className="font-serif text-lg text-[#C9A961]">Scheduled.</p>
              <p className="mt-1 text-xs text-[#C9BEA8]">
                Ayrshare accepted the post for {selected.length} platform
                {selected.length > 1 ? "s" : ""}.
              </p>
              <Link
                to="/admin/calendar"
                data-testid="composer-view-calendar"
                className="mt-3 inline-flex items-center gap-1 text-sm text-[#C9A961] hover:underline"
              >
                <CalendarClock className="h-3.5 w-3.5" />
                View in Calendar
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          )}

          {stage === "done" && scheduleResult?.error && (
            <div
              data-testid="composer-schedule-error"
              className="rounded-2xl border border-[#B85A4A]/60 bg-[#B85A4A]/10 p-4 text-sm"
            >
              <p className="font-medium text-[#B85A4A]">Ayrshare rejected the post — response verbatim:</p>
              <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words text-[10px] text-[#C9BEA8]">
                {scheduleResult.error}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
